/**
 * Vendor file releases — immutable copies under `vendor-releases/{orgId}/{projectId}/...`
 * via Cloud Function `releaseProjectFileToVendor`.
 */

import { db } from './firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import type { ProjectFileStreamKind, RfqHubFileRelease, VendorFileRelease } from '../types';
import { isThreeDRawFile } from './projectFileThreeD';

const SUBCOLLECTION = 'vendorFileReleases';

/** Stream kinds eligible for release to vendors (CNC packages). 2D PDFs are client-portal only. */
export type VendorReleasableStreamKind = Extract<ProjectFileStreamKind, 'general'>;

export function isVendorReleasableStreamKind(k: ProjectFileStreamKind): k is VendorReleasableStreamKind {
  return k === 'general';
}

export function defaultVendorDisplayLabel(_kind: VendorReleasableStreamKind): string {
  return 'CNC Files';
}

function mapReleaseDoc(d: QueryDocumentSnapshot<DocumentData>): VendorFileRelease {
  const data = d.data();
  return {
    id: d.id,
    rfqProjectId: String(data.rfqProjectId || ''),
    supplierOrgId: String(data.supplierOrgId || ''),
    dealId: String(data.dealId || ''),
    sourceFileId: String(data.sourceFileId || ''),
    sourceStreamId: String(data.sourceStreamId || ''),
    sourceStreamTitle: String(data.sourceStreamTitle || ''),
    sourceProjectId: String(data.sourceProjectId || ''),
    versionIndex: typeof data.versionIndex === 'number' ? data.versionIndex : undefined,
    versionLabel: data.versionLabel ? String(data.versionLabel) : undefined,
    displayLabel: String(data.displayLabel || ''),
    releaseVersionIndex: typeof data.releaseVersionIndex === 'number' ? data.releaseVersionIndex : 0,
    storagePath: String(data.storagePath || data.releasedStoragePath || ''),
    releasedUrl: String(data.releasedUrl || ''),
    fileName: String(data.fileName || data.name || ''),
    mimeType: data.mimeType ? String(data.mimeType) : undefined,
    sizeBytes: typeof data.sizeBytes === 'number' ? data.sizeBytes : typeof data.size === 'number' ? data.size : undefined,
    releasedByUid: String(data.releasedByUid || data.releasedBy || ''),
    releasedByName: data.releasedByName ? String(data.releasedByName) : undefined,
    createdAt: data.createdAt?.toDate?.() ?? data.releasedAt?.toDate?.() ?? new Date(),
  };
}

export function subscribeVendorFileReleases(
  rfqProjectId: string,
  supplierOrgId: string,
  onData: (releases: VendorFileRelease[]) => void,
  onError?: (err: Error) => void,
): () => void {
  const q = query(
    collection(db, 'rfqProjects', rfqProjectId, SUBCOLLECTION),
    where('supplierOrgId', '==', supplierOrgId),
  );
  return onSnapshot(
    q,
    (snap) => {
      const releases = snap.docs
        .map(mapReleaseDoc)
        .sort((a, b) => (b.releaseVersionIndex ?? 0) - (a.releaseVersionIndex ?? 0));
      onData(releases);
    },
    (err) => onError?.(err),
  );
}

export type ReleaseProjectFileToVendorInput = {
  rfqProjectId: string;
  supplierOrgId: string;
  dealId: string;
  projectFileId: string;
  displayLabel?: string;
};

export type ReleaseProjectFileToVendorResult = {
  releaseId: string;
  releasedUrl: string;
  releasedStoragePath: string;
  name: string;
  releaseVersionIndex: number;
};

export async function releaseProjectFileToVendor(
  input: ReleaseProjectFileToVendorInput,
): Promise<ReleaseProjectFileToVendorResult> {
  const functions = getFunctions(undefined, 'us-central1');
  const callable = httpsCallable<ReleaseProjectFileToVendorInput, ReleaseProjectFileToVendorResult>(
    functions,
    'releaseProjectFileToVendor',
  );
  const result = await callable(input);
  return result.data;
}

export async function getVendorFileRelease(
  rfqProjectId: string,
  releaseId: string,
): Promise<VendorFileRelease | null> {
  const { getDoc } = await import('firebase/firestore');
  const snap = await getDoc(doc(db, 'rfqProjects', rfqProjectId, SUBCOLLECTION, releaseId));
  if (!snap.exists()) return null;
  return mapReleaseDoc(snap as QueryDocumentSnapshot<DocumentData>);
}

export function cadworkReleasesForPackage(
  pkg: { cncPackagePayload?: { releasedFileIds?: string[] }; supplierOrgId?: string },
  vendorReleases: VendorFileRelease[],
  orgId: string | undefined,
): VendorFileRelease[] {
  const packageReleaseIds = pkg.cncPackagePayload?.releasedFileIds;
  const filtered = vendorReleases.filter(r => {
    if (!orgId || r.supplierOrgId !== orgId) return false;
    if (packageReleaseIds?.length) {
      return Boolean(r.id && packageReleaseIds.includes(r.id));
    }
    return true;
  });
  if (!packageReleaseIds?.length) return filtered;
  const byId = new Map(filtered.filter(r => r.id).map(r => [r.id as string, r]));
  return packageReleaseIds
    .map(id => byId.get(id))
    .filter((r): r is VendorFileRelease => Boolean(r));
}

export type SupplierThreeDDownload = {
  id: string;
  releasedUrl: string;
  fileName: string;
};

export function isHubThreeDRelease(
  release: Pick<RfqHubFileRelease, 'sourceStreamKind' | 'fileName' | 'mimeType'>,
): boolean {
  if (release.sourceStreamKind === 'three_d_raw') return true;
  return isThreeDRawFile({ name: release.fileName || '', type: release.mimeType || '' });
}

export function threeDDownloadsForPackage(
  pkg: {
    rfqProjectId?: string;
    hubFileReleaseId?: string;
    cncPackagePayload?: { releasedFileIds?: string[] };
    supplierOrgId?: string;
    releaseVersionIndex?: number;
    quotePlaceholder?: boolean;
    legacyImport?: boolean;
  },
  vendorReleases: VendorFileRelease[],
  hubReleases: RfqHubFileRelease[],
  orgId: string | undefined,
): SupplierThreeDDownload[] {
  // Quote placeholders / pre-portal packages have no staged .3D — never fall through
  // to other hub releases on the project.
  if (pkg.quotePlaceholder || (pkg.legacyImport && !(pkg.hubFileReleaseId || '').trim())) {
    return [];
  }

  const out: SupplierThreeDDownload[] = [];
  const seen = new Set<string>();
  const add = (id: string | undefined, url: string | undefined, fileName: string | undefined) => {
    const trimmed = (url || '').trim();
    if (!id || !trimmed || seen.has(id)) return;
    seen.add(id);
    out.push({ id, releasedUrl: trimmed, fileName: fileName || 'model.3d' });
  };

  for (const release of cadworkReleasesForPackage(pkg, vendorReleases, orgId)) {
    add(release.id, release.releasedUrl, release.fileName);
  }

  const rfqId = (pkg.rfqProjectId || '').trim();
  const hubThreeD = hubReleases.filter((release) => {
    if (!isHubThreeDRelease(release)) return false;
    const releaseRfq = (release.rfqProjectId || '').trim();
    if (rfqId && releaseRfq && releaseRfq !== rfqId) return false;
    return true;
  });

  const linkedHubId = (pkg.hubFileReleaseId || '').trim();
  const linked = linkedHubId ? hubThreeD.find((release) => release.id === linkedHubId) : undefined;
  if (linked) add(linked.id, linked.releasedUrl, linked.fileName);

  const versionIdx = typeof pkg.releaseVersionIndex === 'number' ? pkg.releaseVersionIndex : undefined;
  const versionMatched =
    versionIdx != null
      ? hubThreeD.filter((release) => release.releaseVersionIndex === versionIdx)
      : [];
  const rest = versionMatched.length ? versionMatched : hubThreeD;
  for (const release of rest) {
    add(release.id, release.releasedUrl, release.fileName);
  }

  return out;
}

export function triggerThreeDFileDownload(release: {
  releasedUrl?: string;
  fileName?: string;
}): boolean {
  const url = release.releasedUrl?.trim();
  if (!url) return false;
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.target = '_blank';
  anchor.rel = 'noopener noreferrer';
  anchor.download = release.fileName || 'model.3d';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  return true;
}

/** @deprecated Prefer {@link triggerThreeDFileDownload}. */
export function triggerCadworkFileDownload(release: VendorFileRelease): boolean {
  return triggerThreeDFileDownload(release);
}
